import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createTrade, formatApiError, getQuote, toastApiError } from "@/lib/api";
import { fmtUsd, fmtQty, isCryptoSymbol } from "@/lib/format";
import { friendlyName, pricePrecision } from "@/lib/symbols";

/**
 * QuickTradeDialog — the ChartPro SELL/BUY quick-action ticket for the
 * desk. Opens with a target { portfolioId, symbol, side, price }, fetches
 * a fresh live quote, lets the user tweak the quantity, and submits a
 * market order. Autopilot-lock is enforced up-stream in ChartPro (buttons
 * disappear) AND by the backend (409). This dialog is only mounted when
 * we already know Autopilot is OFF, but we still surface the backend
 * error clearly if a race happens.
 */
export default function QuickTradeDialog({
  open,
  intent,
  portfolio,
  onClose,
  onFilled,
}) {
  const [qtyStr, setQtyStr] = useState("");
  const [freshQuote, setFreshQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const symbol = intent?.symbol || "";
  const side = intent?.side || "BUY";
  const seedPrice = intent?.price ?? null;
  const price = freshQuote?.price ?? seedPrice ?? 0;
  const precision = useMemo(() => pricePrecision(symbol), [symbol]);
  const cash = Number(portfolio?.cash || 0);
  const held = useMemo(
    () =>
      (portfolio?.positions || []).find((p) => p.symbol === symbol)?.quantity || 0,
    [portfolio, symbol],
  );

  useEffect(() => {
    if (!open || !symbol) return;
    setErr(null);
    setFreshQuote(null);
    setQtyStr("");
    getQuote(symbol)
      .then(setFreshQuote)
      .catch(() => setFreshQuote(null));
  }, [open, symbol]);

  // Suggest a first-fit quantity when the quote arrives.
  useEffect(() => {
    if (!open || !price || qtyStr) return;
    if (side === "SELL") {
      if (held > 0) setQtyStr(String(held.toFixed(isCryptoSymbol(symbol) ? 8 : 4)));
      return;
    }
    // For BUY, propose ~5% of cash worth
    if (cash > 0 && price > 0) {
      const dollars = Math.max(1, cash * 0.05);
      const q = dollars / price;
      if (q > 0)
        setQtyStr(String(q.toFixed(isCryptoSymbol(symbol) ? 8 : 4)));
    }
  }, [open, price, cash, held, side, symbol, qtyStr]);

  const qty = Number(qtyStr) || 0;
  const total = qty * price;
  const canSubmit =
    !!portfolio?.id &&
    qty > 0 &&
    price > 0 &&
    total >= 1 &&
    (side === "SELL" ? qty <= held + 1e-9 : total <= cash + 0.01);

  let hint = null;
  if (qty > 0 && price > 0) {
    if (total < 1) hint = `Trade value ${fmtUsd(total)} is below the $1.00 minimum`;
    else if (side === "BUY" && total > cash + 0.01) {
      const maxQty = cash / price;
      hint = `Insufficient cash — max ${fmtQty(maxQty, symbol)} ${symbol}`;
    } else if (side === "SELL" && qty > held + 1e-9) {
      hint = held > 0
        ? `You only hold ${fmtQty(held, symbol)} ${symbol}`
        : `You do not hold ${symbol}`;
    }
  }

  const submit = async () => {
    if (!portfolio?.id) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await createTrade(portfolio.id, {
        symbol,
        side,
        quantity: qty,
        source: "manual",
      });
      toast.success(`${side} ${fmtQty(qty, symbol)} ${symbol} filled`);
      onFilled?.(res);
      onClose?.();
    } catch (e) {
      // 409 (autopilot race) surfaces here — show it inline AND as a toast.
      // toastApiError skips 401s so we never leave a "Not authenticated"
      // toast on the /login page after a mid-action session expiry.
      const msg = formatApiError(e);
      setErr(msg);
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const isAutopilotErr =
    err && /autopilot is on/i.test(err);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose?.();
      }}
    >
      <DialogContent
        data-testid="quick-trade-dialog"
        className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]"
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-baseline gap-3 font-heading tracking-widest"
            data-testid="quick-trade-title"
          >
            <span
              style={{
                color:
                  side === "BUY"
                    ? "var(--aurion-gold)"
                    : "var(--aurion-purple-metal)",
              }}
            >
              {side}
            </span>
            <span className="text-[var(--aurion-text)]">{symbol}</span>
            <span className="ml-auto font-mono text-[13px] tabular-nums text-[var(--aurion-muted-strong)]">
              {price ? Number(price).toFixed(precision) : "—"}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1 flex flex-col gap-3">
          <div className="text-[11px] text-[var(--aurion-muted)]">
            {friendlyName(symbol, symbol)} · Portfolio{" "}
            <span className="text-[var(--aurion-text)]">
              {portfolio?.name || "—"}
            </span>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Quantity
            </Label>
            <Input
              data-testid="quick-trade-qty"
              type="number"
              step={isCryptoSymbol(symbol) ? "0.00000001" : "0.0001"}
              min="0"
              value={qtyStr}
              onChange={(e) => setQtyStr(e.target.value)}
              className="mt-1 h-10 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] font-mono"
              autoFocus
            />
            <div className="mt-1 flex items-baseline justify-between text-[10px] text-[var(--aurion-muted)]">
              <span>
                {side === "SELL"
                  ? `Held: ${fmtQty(held, symbol)} ${symbol}`
                  : `Cash: ${fmtUsd(cash)}`}
              </span>
              <span>Est. total {fmtUsd(total)}</span>
            </div>
          </div>

          {hint ? (
            <div
              data-testid="quick-trade-hint"
              className="text-[11px] text-amber-300"
            >
              {hint}
            </div>
          ) : null}

          {err ? (
            <div
              data-testid="quick-trade-error"
              className={`flex items-start gap-2 border px-2 py-1.5 text-[11px] ${
                isAutopilotErr
                  ? "border-[var(--aurion-gold)]/40 bg-[var(--aurion-gold)]/10 text-[var(--aurion-gold)]"
                  : "border-red-500/40 bg-red-500/10 text-red-300"
              }`}
            >
              {isAutopilotErr ? <Lock className="mt-0.5 h-3.5 w-3.5" /> : null}
              <span>{err}</span>
            </div>
          ) : null}
        </div>

        <DialogFooter className="mt-3 flex gap-2">
          <Button
            data-testid="quick-trade-cancel"
            variant="ghost"
            onClick={() => onClose?.()}
            className="border border-[var(--aurion-border)] text-[var(--aurion-muted)]"
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            data-testid="quick-trade-submit"
            onClick={submit}
            disabled={busy || !canSubmit}
            className={`font-heading tracking-widest ${
              side === "BUY"
                ? "border border-[var(--aurion-gold)]/60 bg-[var(--aurion-gold)] text-black hover:bg-[var(--aurion-gold)]/90"
                : "border border-[var(--aurion-purple-metal)]/60 bg-[var(--aurion-purple-metal)]/20 text-[var(--aurion-purple-metal)] hover:bg-[var(--aurion-purple-metal)]/30"
            }`}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Place {side} {qty ? fmtQty(qty, symbol) : ""} {symbol}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
